import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Save, Plus, Trash2, Loader2, X, Copy, Pencil, ExternalLink } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const yen = (n) => `¥${Number(n || 0).toLocaleString()}`;

const STATUS = {
  draft: { label: "下書き", cls: "bg-slate-500" },
  requested: { label: "依頼受付", cls: "bg-blue-600" },
  sent: { label: "送付済み", cls: "bg-amber-600" },
  accepted: { label: "受注", cls: "bg-emerald-600" },
  rejected: { label: "見送り", cls: "bg-red-600" },
};

const inputCls = "bg-slate-700 border-slate-600 text-white";

export default function QuoteAdminPanel({ adminApi, getAuthHeaders, plans }) {
  const [settings, setSettings] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [createForm, setCreateForm] = useState({
    title: "", name: "", company: "", email: "", phone: "",
    plan_id: "", employee_count: 10, billing_cycle: "monthly", option_ids: [], option_qtys: {}, status: "draft", message: "",
  });

  const emptyForm = () => ({
    title: "", name: "", company: "", email: "", phone: "",
    plan_id: "", employee_count: 10, billing_cycle: "monthly", option_ids: [], option_qtys: {}, status: "draft", message: "",
  });

  const load = async () => {
    try {
      const headers = getAuthHeaders();
      const [s, q] = await Promise.all([
        axios.get(`${adminApi}/admin/quote-settings`, { headers }),
        axios.get(`${adminApi}/admin/quotes`, { headers }),
      ]);
      setSettings(s.data);
      setQuotes(q.data || []);
    } catch (e) {
      toast.error("見積データの取得に失敗しました");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveSettings = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${adminApi}/admin/quote-settings`, settings, { headers: getAuthHeaders() });
      setSettings(res.data);
      toast.success("見積設定を保存しました");
    } catch (e) {
      const status = e.response?.status;
      if (status === 401) {
        toast.error("セッションの有効期限が切れました。再度ログインしてください。");
        localStorage.removeItem("admin_token");
        setTimeout(() => { window.location.href = "/saas-admin/login"; }, 1500);
      } else if (status === 403) {
        toast.error(e.response?.data?.detail || "この操作にはスーパー管理者権限が必要です。一度ログアウトして再ログインのうえお試しください。");
      } else if (!e.response) {
        toast.error("ネットワークエラーで保存できませんでした。通信環境をご確認のうえ、ページを再読み込みして再度お試しください。");
      } else {
        toast.error(e.response?.data?.detail || `保存に失敗しました（コード: ${status || "不明"}）`);
      }
    } finally {
      setSaving(false);
    }
  };

  const updateOption = (idx, key, val) => {
    const options = [...(settings.options || [])];
    options[idx] = { ...options[idx], [key]: val };
    setSettings({ ...settings, options });
  };
  const addOption = () => {
    setSettings({
      ...settings,
      options: [...(settings.options || []), { id: `opt_${Date.now()}`, name: "", description: "", price: 0, billing: "monthly" }],
    });
  };
  const removeOption = (idx) => {
    const options = [...(settings.options || [])];
    options.splice(idx, 1);
    setSettings({ ...settings, options });
  };

  const createQuote = async () => {
    if (!createForm.plan_id) {
      toast.error("プランを選択してください");
      return;
    }
    setCreating(true);
    const payload = {
      title: createForm.title,
      customer: { name: createForm.name, company: createForm.company, email: createForm.email, phone: createForm.phone },
      employee_count: Number(createForm.employee_count) || 1,
      plan_id: createForm.plan_id,
      billing_cycle: createForm.billing_cycle,
      options: createForm.option_ids.map((id) => ({ id, qty: Number(createForm.option_qtys[id]) || 1 })),
      status: createForm.status,
      message: createForm.message,
    };
    try {
      if (editingId) {
        await axios.put(`${adminApi}/admin/quotes/${editingId}`, payload, { headers: getAuthHeaders() });
        toast.success("見積を更新しました");
      } else {
        await axios.post(`${adminApi}/admin/quotes`, payload, { headers: getAuthHeaders() });
        toast.success("見積を作成しました");
      }
      setShowCreate(false);
      setEditingId(null);
      setCreateForm(emptyForm());
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || (editingId ? "更新に失敗しました" : "作成に失敗しました"));
    } finally {
      setCreating(false);
    }
  };

  const openCreate = () => {
    setEditingId(null);
    setCreateForm(emptyForm());
    setShowCreate(true);
  };

  const openEdit = (q) => {
    setEditingId(q.id);
    setCreateForm({
      title: q.title || "",
      name: q.customer?.name || "",
      company: q.customer?.company || "",
      email: q.customer?.email || "",
      phone: q.customer?.phone || "",
      plan_id: q.plan_id || "",
      employee_count: q.employee_count || 1,
      billing_cycle: q.billing_cycle || "monthly",
      option_ids: (q.options || []).map((o) => o.id),
      option_qtys: Object.fromEntries((q.options || []).map((o) => [o.id, o.qty || 1])),
      status: q.status || "draft",
      message: q.message || "",
    });
    setShowCreate(true);
  };

  const duplicateQuote = async (q) => {
    try {
      await axios.post(`${adminApi}/admin/quotes/${q.id}/duplicate`, {}, { headers: getAuthHeaders() });
      toast.success("見積を複製しました（変種を作成）");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "複製に失敗しました");
    }
  };

  const changeStatus = async (q, status) => {
    try {
      await axios.put(`${adminApi}/admin/quotes/${q.id}`, { status }, { headers: getAuthHeaders() });
      setQuotes((prev) => prev.map((x) => (x.id === q.id ? { ...x, status } : x)));
    } catch (e) {
      toast.error("ステータス更新に失敗しました");
    }
  };

  const deleteQuote = async (q) => {
    if (!window.confirm(`見積 ${q.quote_number} を削除しますか？`)) return;
    try {
      await axios.delete(`${adminApi}/admin/quotes/${q.id}`, { headers: getAuthHeaders() });
      setQuotes((prev) => prev.filter((x) => x.id !== q.id));
      toast.success("削除しました");
    } catch (e) {
      toast.error("削除に失敗しました");
    }
  };

  const openPdf = async (q) => {
    const win = window.open("", "_blank");
    try {
      const res = await axios.get(`${adminApi}/admin/quotes/${q.id}/pdf`, { headers: getAuthHeaders(), responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      if (win) {
        win.location.href = url;
      } else {
        window.location.href = url;
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      if (win) win.close();
      toast.error("PDF生成に失敗しました");
    }
  };

  const toggleCreateOption = (id) => {
    setCreateForm((f) => {
      const has = f.option_ids.includes(id);
      const option_ids = has ? f.option_ids.filter((x) => x !== id) : [...f.option_ids, id];
      const option_qtys = { ...f.option_qtys };
      if (has) delete option_qtys[id];
      else option_qtys[id] = option_qtys[id] || 1;
      return { ...f, option_ids, option_qtys };
    });
  };

  const setCreateOptionQty = (id, val) => {
    setCreateForm((f) => ({ ...f, option_qtys: { ...f.option_qtys, [id]: val } }));
  };

  if (loading || !settings) {
    return (
      <div className="flex items-center justify-center h-40">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* 見積設定 */}
      <div className="rounded-xl border border-slate-700 bg-slate-800 p-6">
        <h3 className="text-lg font-bold text-white mb-4">見積設定</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label className="text-white">初期導入費用（税抜・円）</Label>
            <Input type="number" value={settings.initial_fee} onChange={(e) => setSettings({ ...settings, initial_fee: Number(e.target.value) })} className={inputCls} data-testid="qs-initial-fee" />
          </div>
          <div className="space-y-2">
            <Label className="text-white">追加従業員 単価（1名/月・税抜）</Label>
            <Input type="number" value={settings.per_employee_fee} onChange={(e) => setSettings({ ...settings, per_employee_fee: Number(e.target.value) })} className={inputCls} data-testid="qs-per-employee-fee" />
          </div>
          <div className="space-y-2">
            <Label className="text-white">消費税率（%）</Label>
            <Input type="number" value={settings.tax_rate} onChange={(e) => setSettings({ ...settings, tax_rate: Number(e.target.value) })} className={inputCls} data-testid="qs-tax-rate" />
          </div>
          <div className="space-y-2">
            <Label className="text-white">見積有効期限（日）</Label>
            <Input type="number" value={settings.validity_days} onChange={(e) => setSettings({ ...settings, validity_days: Number(e.target.value) })} className={inputCls} data-testid="qs-validity-days" />
          </div>
        </div>

        <div className="mt-6">
          <div className="flex items-center justify-between mb-2">
            <Label className="text-white">オプション</Label>
            <Button size="sm" variant="outline" onClick={addOption} className="text-white border-slate-600" data-testid="qs-add-option">
              <Plus className="w-4 h-4 mr-1" />
              追加
            </Button>
          </div>
          <div className="space-y-2">
            {(settings.options || []).map((o, idx) => (
              <div key={o.id || idx} className="grid grid-cols-12 gap-2 items-center" data-testid={`qs-option-row-${idx}`}>
                <Input value={o.name} placeholder="名称" onChange={(e) => updateOption(idx, "name", e.target.value)} className={`${inputCls} col-span-3`} />
                <Input value={o.description} placeholder="説明" onChange={(e) => updateOption(idx, "description", e.target.value)} className={`${inputCls} col-span-4`} />
                <Input type="number" value={o.price} placeholder="金額" onChange={(e) => updateOption(idx, "price", Number(e.target.value))} className={`${inputCls} col-span-2`} />
                <select value={o.billing} onChange={(e) => updateOption(idx, "billing", e.target.value)} className={`${inputCls} col-span-2 rounded-md h-10 px-2 text-sm`}>
                  <option value="monthly">月額</option>
                  <option value="onetime">初期のみ</option>
                </select>
                <button onClick={() => removeOption(idx)} className="col-span-1 text-red-400 hover:text-red-300 flex justify-center" data-testid={`qs-remove-option-${idx}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-6 space-y-2">
          <Label className="text-white">見積書 備考</Label>
          <textarea value={settings.notes} onChange={(e) => setSettings({ ...settings, notes: e.target.value })} rows={3} className={`${inputCls} w-full rounded-md px-3 py-2 text-sm`} data-testid="qs-notes" />
        </div>

        <Button onClick={saveSettings} disabled={saving} className="mt-4 bg-emerald-600 hover:bg-emerald-700" data-testid="qs-save">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          設定を保存
        </Button>
      </div>

      {/* 見積一覧 */}
      <div className="rounded-xl border border-slate-700 bg-slate-800 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-white">見積一覧 <span className="text-slate-400 text-sm">({quotes.length})</span></h3>
          <Button size="sm" onClick={openCreate} className="bg-emerald-600 hover:bg-emerald-700" data-testid="quote-create-open">
            <Plus className="w-4 h-4 mr-1" />
            新規見積作成
          </Button>
        </div>
        {quotes.length === 0 ? (
          <p className="text-slate-400 text-sm py-6 text-center">まだ見積はありません。</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-white">
              <thead>
                <tr className="text-slate-400 border-b border-slate-700 text-left">
                  <th className="py-2 pr-3">見積番号</th>
                  <th className="py-2 pr-3">顧客</th>
                  <th className="py-2 pr-3">プラン</th>
                  <th className="py-2 pr-3 text-right">初回合計(税込)</th>
                  <th className="py-2 pr-3">ステータス</th>
                  <th className="py-2 pr-3">作成日</th>
                  <th className="py-2 text-right">操作</th>
                </tr>
              </thead>
              <tbody>
                {quotes.map((q) => (
                  <tr key={q.id} className="border-b border-slate-700/60" data-testid={`quote-row-${q.quote_number}`}>
                    <td className="py-2 pr-3 font-mono text-xs">
                      {q.quote_number}
                      {q.title && <div className="font-sans text-[11px] text-amber-300 not-italic mt-0.5">{q.title}</div>}
                    </td>
                    <td className="py-2 pr-3">
                      <div>{q.customer?.company || "-"}</div>
                      <div className="text-slate-400 text-xs">{q.customer?.name}</div>
                    </td>
                    <td className="py-2 pr-3">
                      {q.plan_name}
                      <span className="text-slate-400 text-xs ml-1">/ {q.estimate?.billing_label} / {q.employee_count}名</span>
                    </td>
                    <td className="py-2 pr-3 text-right tabular-nums">{yen(q.estimate?.initial_payment_total)}</td>
                    <td className="py-2 pr-3">
                      <select
                        value={q.status}
                        onChange={(e) => changeStatus(q, e.target.value)}
                        className={`rounded px-2 py-1 text-xs text-white ${STATUS[q.status]?.cls || "bg-slate-600"}`}
                        data-testid={`quote-status-${q.quote_number}`}
                      >
                        {Object.entries(STATUS).map(([k, v]) => (
                          <option key={k} value={k} className="bg-slate-800">
                            {v.label}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="py-2 pr-3 text-slate-400 text-xs">{(q.created_at || "").slice(0, 10)}</td>
                    <td className="py-2 text-right whitespace-nowrap">
                      <button onClick={() => openEdit(q)} className="text-sky-400 hover:text-sky-300 mr-3 inline-flex items-center" title="編集" data-testid={`quote-edit-${q.quote_number}`}>
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button onClick={() => duplicateQuote(q)} className="text-violet-400 hover:text-violet-300 mr-3 inline-flex items-center" title="複製（変種を作成）" data-testid={`quote-duplicate-${q.quote_number}`}>
                        <Copy className="w-4 h-4" />
                      </button>
                      <button onClick={() => openPdf(q)} className="text-emerald-400 hover:text-emerald-300 mr-3 inline-flex items-center" title="PDFを新しいタブで開く" data-testid={`quote-pdf-${q.quote_number}`}>
                        <ExternalLink className="w-4 h-4" />
                      </button>
                      <button onClick={() => deleteQuote(q)} className="text-red-400 hover:text-red-300 inline-flex items-center" title="削除" data-testid={`quote-delete-${q.quote_number}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* 新規見積作成 / 編集モーダル */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => { setShowCreate(false); setEditingId(null); }}>
          <div className="bg-slate-800 rounded-2xl border border-slate-700 w-full max-w-lg max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()} data-testid="quote-create-dialog">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white">{editingId ? "見積を編集" : "新規見積作成"}</h3>
              <button onClick={() => { setShowCreate(false); setEditingId(null); }} className="text-slate-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <Label className="text-white text-sm">件名 / 案名（任意・変種の識別用）</Label>
                <Input placeholder="例：スタンダード案 / プレミアム案" value={createForm.title} onChange={(e) => setCreateForm({ ...createForm, title: e.target.value })} className={`${inputCls} mt-1`} data-testid="qc-title" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Input placeholder="担当者名" value={createForm.name} onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })} className={inputCls} data-testid="qc-name" />
                <Input placeholder="会社名" value={createForm.company} onChange={(e) => setCreateForm({ ...createForm, company: e.target.value })} className={inputCls} data-testid="qc-company" />
                <Input type="email" placeholder="メール" value={createForm.email} onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })} className={inputCls} data-testid="qc-email" />
                <Input placeholder="電話" value={createForm.phone} onChange={(e) => setCreateForm({ ...createForm, phone: e.target.value })} className={inputCls} data-testid="qc-phone" />
              </div>
              <div>
                <Label className="text-white text-sm">プラン</Label>
                <select value={createForm.plan_id} onChange={(e) => setCreateForm({ ...createForm, plan_id: e.target.value })} className={`${inputCls} w-full rounded-md h-10 px-2 mt-1`} data-testid="qc-plan">
                  <option value="">選択してください</option>
                  {(plans || []).filter((p) => p.is_active !== false).map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}（〜{p.max_employees}名 / {yen(p.price_monthly)}〜）
                    </option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-white text-sm">従業員数</Label>
                  <Input type="number" min={1} value={createForm.employee_count} onChange={(e) => setCreateForm({ ...createForm, employee_count: e.target.value })} className={`${inputCls} mt-1`} data-testid="qc-employee-count" />
                </div>
                <div>
                  <Label className="text-white text-sm">課金サイクル</Label>
                  <select value={createForm.billing_cycle} onChange={(e) => setCreateForm({ ...createForm, billing_cycle: e.target.value })} className={`${inputCls} w-full rounded-md h-10 px-2 mt-1`} data-testid="qc-billing">
                    <option value="monthly">月額</option>
                    <option value="yearly">年額</option>
                  </select>
                </div>
              </div>
              {(settings.options || []).length > 0 && (
                <div>
                  <Label className="text-white text-sm">オプション</Label>
                  <div className="space-y-2 mt-1">
                    {settings.options.map((o) => {
                      const selected = createForm.option_ids.includes(o.id);
                      return (
                        <div key={o.id} className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => toggleCreateOption(o.id)}
                            className={`flex-1 text-left px-3 py-1.5 rounded-md text-xs border transition-colors ${selected ? "bg-emerald-600/20 border-emerald-600 text-white" : "border-slate-600 text-slate-300 hover:border-slate-500"}`}
                            data-testid={`qc-option-${o.id}`}
                          >
                            {o.name} <span className="text-slate-400">（{yen(o.price)}{o.billing === "onetime" ? "・初期" : "/月"}）</span>
                          </button>
                          {selected && (
                            <div className="flex items-center gap-1 flex-shrink-0">
                              <span className="text-slate-400 text-xs">数量</span>
                              <Input
                                type="number"
                                min={1}
                                value={createForm.option_qtys[o.id] ?? 1}
                                onChange={(e) => setCreateOptionQty(o.id, e.target.value)}
                                className={`${inputCls} w-20 text-center`}
                                data-testid={`qc-option-qty-${o.id}`}
                              />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              <div>
                <Label className="text-white text-sm">ステータス</Label>
                <select value={createForm.status} onChange={(e) => setCreateForm({ ...createForm, status: e.target.value })} className={`${inputCls} w-full rounded-md h-10 px-2 mt-1`} data-testid="qc-status">
                  {Object.entries(STATUS).map(([k, v]) => (
                    <option key={k} value={k}>{v.label}</option>
                  ))}
                </select>
              </div>
              <Button onClick={createQuote} disabled={creating} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="qc-submit">
                {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : (editingId ? <Save className="w-4 h-4 mr-2" /> : <Plus className="w-4 h-4 mr-2" />)}
                {editingId ? "更新する" : "作成する"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
